<script context="module" lang="ts">
    export type ContextualMenuButtonStyle = 'platter' | 'non-platter';
</script>

<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import type { ContextualMenuConfig } from './types';

    export let config: ContextualMenuConfig;
    export let buttonStyle: ContextualMenuButtonStyle = 'platter';
    export let hasMaterial: boolean = true;

    // Expose the context menu button element to any consumers
    // Useful for things like handling mobile touch events on
    // components that do not expose a context menu button visually
    // in mobile viewports (ex: Report A Concern, EllipseLockup, etc.)
    export let contextualMenuButtonElement: HTMLElement = null;

    // By default, the contextual menu will render in document.body unless specified in the contextual menu config
    // https://github.pie.apple.com/amp-web/components/blob/main/src/components/contextual-menu-button/contextual-menu-button.tsx#L96-L98
    // Setting an HTML element will render the <amp-contextual-menu /> element within that element
    // Used by Classical where the contextual menu config comes down from the server
    export let parentElement: HTMLElement = null;

    /**
     * translate function provided by the parent app.
     */
    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;

    let open: boolean = false;

    const dispatch = createEventDispatcher();

    $: updatedConfig = !!parentElement ? { ...config, parentElement } : config;

    function toggleMenuVisibility() {
        open = !open;
        dispatch('contextMenuVisibilityChange', { open });
    }

    function handleContextualMenuOpen() {
        toggleMenuVisibility();
    }

    function handleContextualMenuFocused() {
        if (open) {
            toggleMenuVisibility();
        }

        dispatch('triggerFocus');
    }
</script>

<amp-contextual-menu-button
    config={updatedConfig}
    on:contextualMenuOpen={handleContextualMenuOpen}
    on:contextualMenuFocused={handleContextualMenuFocused}
    bind:this={contextualMenuButtonElement}
>
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <span
        aria-label={translateFn('AMP.Shared.Truncate.More')}
        class="more-button"
        class:more-button--platter={buttonStyle === 'platter'}
        class:more-button--non-platter={buttonStyle === 'non-platter'}
        class:more-button--material={hasMaterial && buttonStyle === 'platter'}
        data-testid="more-button"
        slot="trigger-content"
        on:mouseover
        on:focus
    >
        <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            class="glyph"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                fill="var(--iconCircleFill, transparent)"
                cx="14"
                cy="14"
                r="14"
            />
            <path
                fill="var(--iconEllipsisFill, white)"
                d="M10.105 14c0-.87-.687-1.55-1.564-1.55-.862 0-1.557.695-1.557 1.55 0 .848.695 1.55 1.557 1.55.855 0 1.564-.702 1.564-1.55zm5.437 0c0-.87-.68-1.55-1.542-1.55A1.55 1.55 0 0012.45 14c0 .848.695 1.55 1.55 1.55.848 0 1.542-.702 1.542-1.55zm5.474 0c0-.87-.687-1.55-1.557-1.55-.87 0-1.564.695-1.564 1.55 0 .848.694 1.55 1.564 1.55.848 0 1.557-.702 1.557-1.55z"
            />
        </svg>
    </span>
</amp-contextual-menu-button>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/helpers' as *;
    @use 'amp/stylekit/core/mixins/materials' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use 'sass:map';

    :global(amp-contextual-menu) {
        --ctxmenu-item-bg: var(--systemStandardThickMaterialSover-default_IC);
        --ctxmenu-item-bg-hover: var(--systemQuinary);
        --borderRule: var(--vibrantDivider, rgba(0, 0, 0, 0.15));
        --ctxmenu-item-min-width: 185px;
        --ctxmenu-z-index: var(--z-contextual-menus);
        --ctxmenu-scrim-z-index: calc(var(--z-contextual-menus) - 1);
        // --ctxmenu-scrim-bg-mobile
        // --ctxmenu-scrim-filter
        // --ctxmenu-item-min-width
        // --ctxmenu-item-color
        // --ctxmenu-item-outline
        // --ctxmenu-item-bg
        // --ctxmenu-item-bg-filter <-- needs to be added to web component
        // --ctxmenu-item-bg-hover

        // Menu focus outline color
        --keyColor-focus-color: #{$focus-color};
        position: relative;
    }

    .more-button {
        // Custom properties are embedded in SVG
        --iconCircleFill: transparent;
        --iconEllipsisFill: var(--contextMenuEllipsisFillOverride, white);
        width: var(--contextMenuButtonSize, #{$action-button-size});
        height: var(--contextMenuButtonSize, #{$action-button-size});
        display: block;
        line-height: 0; // Normalize white space

        :global(svg) {
            width: 100%;
            height: 100%;
            border-radius: inherit;
        }
    }

    .more-button--platter {
        // Custom properties are embedded in SVG
        --iconCircleFill: var(--contextMenuCircleFillOverride, transparent);
        --iconEllipsisFill: var(--contextMenuEllipsisFillOverride, white);
        border-radius: 50%;

        @media (--target-desktop) {
            amp-contextual-menu-button:hover &,
            amp-contextual-menu-button:focus-within & {
                // Custom properties are embedded in SVG
                --iconCircleFill: var(
                    --contextMenuCircleHoverFill,
                    transparent
                );
                --iconEllipsisFill: var(--contextMenuEllipsisHoverFill, white);
            }
        }
    }

    .more-button--non-platter {
        --iconEllipsisFill: var(
            --contextMenuEllipsisFillOverride,
            var(--keyColor)
        );

        :global(button):hover &,
        :global(button):focus-visible & {
            --iconEllipsisFill: var(
                --contextMenuEllipsisHoverFillOverride,
                var(--contextMenuEllipsisFillOverride, var(--keyColor))
            );
        }
    }

    .more-button--material {
        width: var(--contextMenuButtonSize, 30px);
        height: var(--contextMenuButtonSize, 30px);

        @include system-standard-thin-material;

        amp-contextual-menu-button:hover &,
        amp-contextual-menu-button:focus-within & {
            // Custom properties are embedded in SVG
            --iconCircleFill: var(
                --contextMenuCircleHoverFill,
                var(--keyColor)
            );
            --iconEllipsisFill: var(--contextMenuEllipsisHoverFill, white);
            backdrop-filter: none;
            background-color: transparent;
        }

        @supports #{'selector(:has(:focus-visible))'} {
            amp-contextual-menu-button:focus-within & {
                --iconCircleFill: var(
                    --contextMenuCircleFillOverride,
                    transparent
                );
                --iconEllipsisFill: var(
                    --contextMenuEllipsisFillOverride,
                    white
                );

                @include system-standard-thin-material;
            }

            amp-contextual-menu-button:hover &,
            amp-contextual-menu-button:has(:focus-visible) & {
                // Custom properties are embedded in SVG
                --iconCircleFill: var(
                    --contextMenuCircleHoverFill,
                    var(--keyColor)
                );
                --iconEllipsisFill: var(--contextMenuEllipsisHoverFill, white);
                backdrop-filter: none;
                background-color: transparent;
            }
        }
    }
</style>
